import api from '../config/api';
import {
  Member,
  MemberListResponse,
  MemberSearchParams,
  Region,
  MuseumType,
  IndividualMemberDto,
  MemberVignetteRecord,
} from '../types/member';
import { ChangeRequest } from '../types/changeRequest';

// Mock member data
const mockMembers: Member[] = [
  {
    id: '1',
    email: 'curator@acropolis.gr',
    firstName: 'Maria',
    lastName: 'Papadopoulos',
    role: 'member',
    region: 'Attica',
    museumType: 'Archaeological Museum',
    museumName: 'Acropolis Museum',
    position: 'Senior Curator',
    phone: '+30 210 9000900',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    membershipCategory: 'Regular',
    communicationLanguage: 'English',
    acceptedEthics: true,
  },
  {
    id: '2',
    email: 'director@benaki.gr',
    firstName: 'Dimitris',
    lastName: 'Georgiou',
    role: 'member',
    region: 'Attica',
    museumType: 'Art Museum',
    museumName: 'Benaki Museum',
    position: 'Director',
    phone: '+30 210 3671000',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    membershipCategory: 'Regular',
    communicationLanguage: 'English',
    acceptedEthics: true,
  },
  {
    id: '3',
    email: 'archaeologist@thessaloniki.gr',
    firstName: 'Eleni',
    lastName: 'Nikolaou',
    role: 'member',
    region: 'Thessaloniki',
    museumType: 'Archaeological Museum',
    museumName: 'Archaeological Museum of Thessaloniki',
    position: 'Archaeologist',
    phone: '+30 2310 830538',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    membershipCategory: 'Regular',
    communicationLanguage: 'English',
    acceptedEthics: true,
  },
  {
    id: '4',
    email: 'conservator@heraklion.gr',
    firstName: 'Giorgos',
    lastName: 'Kostopoulos',
    role: 'member',
    region: 'Crete',
    museumType: 'Archaeological Museum',
    museumName: 'Heraklion Archaeological Museum',
    position: 'Conservator',
    phone: '+30 2810 279000',
    isActive: false,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    membershipCategory: 'Regular',
    communicationLanguage: 'English',
    acceptedEthics: true,
  },
];

// Mock regions and museum types
const mockRegions: Region[] = [
  { id: '1', name: 'Attica', nameGr: 'Αττική' },
  { id: '2', name: 'Thessaloniki', nameGr: 'Θεσσαλονίκη' },
  { id: '3', name: 'Crete', nameGr: 'Κρήτη' },
  { id: '4', name: 'Peloponnese', nameGr: 'Πελοπόννησος' },
];

const mockMuseumTypes: MuseumType[] = [
  { id: '1', name: 'Archaeological Museum', nameGr: 'Αρχαιολογικό Μουσείο' },
  { id: '2', name: 'Art Museum', nameGr: 'Μουσείο Τέχνης' },
  { id: '3', name: 'History Museum', nameGr: 'Ιστορικό Μουσείο' },
  {
    id: '4',
    name: 'Natural History Museum',
    nameGr: 'Μουσείο Φυσικής Ιστορίας',
  },
];

export const memberService = {
  getMembers: async (
    params: MemberSearchParams = {}
  ): Promise<MemberListResponse> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const { page = 1, limit = 10, filters = {} } = params;

    let filteredMembers = [...mockMembers];

    // Apply filters
    if (filters.region) {
      filteredMembers = filteredMembers.filter(
        member => member.region === filters.region
      );
    }

    if (filters.museumType) {
      filteredMembers = filteredMembers.filter(
        member => member.museumType === filters.museumType
      );
    }

    if (filters.isActive !== undefined) {
      filteredMembers = filteredMembers.filter(
        member => member.isActive === filters.isActive
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredMembers = filteredMembers.filter(
        member =>
          member.firstName.toLowerCase().includes(searchTerm) ||
          member.lastName.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm) ||
          member.museumName?.toLowerCase().includes(searchTerm) ||
          member.position?.toLowerCase().includes(searchTerm)
      );
    }

    // Calculate pagination
    const total = filteredMembers.length;
    const totalPages = Math.ceil(total / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

    return {
      members: paginatedMembers,
      total,
      page,
      limit,
      totalPages,
    };
  },

  getMemberById: async (id: string): Promise<Member | null> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const member = mockMembers.find(m => m.id === id);
    return member || null;
  },

  getRegions: async (): Promise<Region[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    return mockRegions;
  },

  getMuseumTypes: async (): Promise<MuseumType[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    return mockMuseumTypes;
  },

  updateMember: async (
    id: string,
    updates: Partial<Member>
  ): Promise<Member> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const memberIndex = mockMembers.findIndex(m => m.id === id);
    if (memberIndex === -1) {
      throw new Error('Member not found');
    }

    mockMembers[memberIndex] = {
      ...mockMembers[memberIndex],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    return mockMembers[memberIndex];
  },

  getMyVignettes: async (): Promise<MemberVignetteRecord[]> => {
    const response = await api.get<MemberVignetteRecord[]>('/members/me/vignettes');
    return response.data;
  },

  getMyMember: async (): Promise<IndividualMemberDto> => {
    const response = await api.get<IndividualMemberDto>('/members/me');
    return response.data;
  },

  updateMyMember: async (
    member: Partial<IndividualMemberDto>
  ): Promise<ChangeRequest[]> => {
    const response = await api.put<ChangeRequest[]>('/members/me', member);
    return response.data;
  },
};
